/* eslint-disable no-console */
import { useEffect, useRef } from 'react';
import { useChatClient } from '@ably/chat/react';

/**
 * AblyCleanupWrapper - Ensures proper cleanup of Ably chat connections
 * when the wrapped component unmounts.
 *
 * This wrapper should be used INSIDE AblyWrapper (after ChatClientProvider)
 * to ensure proper connection cleanup and prevent memory leaks.
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children - The component(s) to wrap
 * @param {boolean} props.forceCleanup - If true, forces immediate cleanup on unmount
 */
function AblyCleanupWrapper({ children, forceCleanup = true }) {
  const chatClient = useChatClient(); // This will now be available since we're inside ChatClientProvider
  const cleanupExecutedRef = useRef(false);

  useEffect(() => {
    // Reset cleanup flag when component mounts
    cleanupExecutedRef.current = false;

    return () => {
      // Prevent multiple cleanup executions
      if (cleanupExecutedRef.current) {
        return;
      }
      cleanupExecutedRef.current = true;

      if (forceCleanup && chatClient) {
        // eslint-disable-next-line no-console
        console.log('🧹 AblyCleanupWrapper: Executing cleanup on component unmount');

        try {
          // Only handle room cleanup - DO NOT close the underlying Ably connection
          // The AblyAuth hook and AblyWrapper will handle connection cleanup
          if (chatClient.rooms) {
            // eslint-disable-next-line no-console
            console.log('🧹 Cleanup: Allowing Chat component to handle room detachment');
            // Note: Individual room cleanup is handled by the Chat component's useEffect cleanup
          }
        } catch (error) {
          // eslint-disable-next-line no-console
          console.warn('AblyCleanupWrapper: Error during cleanup:', error);
        }
      }
    };
  }, [chatClient, forceCleanup]);

  return children;
}

export default AblyCleanupWrapper;
